import { Box, FormGroup, InputAdornment, TextField } from '@mui/material';
import { FastField } from 'formik';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { CURRENCY } from '../../config/appConfig';
import TextWrapper from './useTextWrapper';

InputWrapper.propTypes = {
	placeholder: PropTypes.string,
	isTextArea: PropTypes.bool,
	rows: PropTypes.number,
	type: PropTypes.string,
	disabled: PropTypes.bool,
	labelClass: PropTypes.string,
	errorClass: PropTypes.string,
	showCurrency: PropTypes.bool,
	readOnly: PropTypes.bool,
};

export default function InputWrapper({
	placeholder,
	isTextArea = false,
	rows = 1,
	type = 'text',
	disabled = false,
	isCurrencyText = false,
	labelClass = '',
	errorClass = '',
	showCurrency = false,
	isTruncateLabel = false,
	readOnly = false,
	label = null,
	classList,
	onBlur,
	onChanges,
	onPaste = null,
	preventSetValue = false,
	...props
}) {
	// Memoized input props and input class
	const inputProps = useMemo(
		() => ({
			style: {
				textAlign: 'start',
				resize: isTextArea ? 'vertical' : 'none',
				opacity: readOnly ? '0.4' : '1',
			},
			readOnly,
		}),
		[isTextArea, readOnly]
	);

	const InputProps = useMemo(
		() => ({
			startAdornment: showCurrency ? (
				<InputAdornment position="start">{CURRENCY}</InputAdornment>
			) : null,
		}),
		[showCurrency]
	);

	const inputClassName = useMemo(
		() => `mt-1 ${isTextArea ? 'input-text-area' : ''} ${classList || ''}`,
		[isTextArea, classList]
	);

	return (
		<FastField name={props.name}>
			{({ field, meta, form }) => {
				const isError = meta.touched && meta.error;

				return (
					<FormGroup className="w-full h-auto">
						<Box className={`flex flex-col items-start ${isTruncateLabel ? 'w-full' : ''}`}>
							<Box className={`flex flex-0 items-center ${isTruncateLabel ? 'w-full' : ''}`}>
								<TextWrapper
									classList={`${isTruncateLabel ? 'truncate-word' : ' '} !text-[11px]`}
									type="caption"
									text={isError && label ? `${label} ` : label}
								/>
								{isError && label ? (
									<span className="!text-red-500 !text-[11px]">*</span>
								) : null}
							</Box>
							<TextField
								sx={{
									'& input[type=number]': {
										MozAppearance: 'textfield',   // Firefox
									},
									'& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
										WebkitAppearance: 'none',    // Chrome, Safari, Edge, Opera
										margin: 0,
									},
								}}
								onPaste={(e) => {
									if (onPaste) {
										e.preventDefault(); // Prevent default paste behavior
										const pastedData = e.clipboardData.getData('Text');
										onPaste(pastedData, field.name, form); // Call onPaste handler
									}
								}}
								error={isError}
								helperText={<span>{meta.touched ? meta.error : ''}</span>}
								FormHelperTextProps={{ className: `${errorClass}` }}
								multiline={isTextArea}
								maxRows={isTextArea ? rows : 1}
								inputProps={inputProps}
								InputLabelProps={{ shrink: false, style: { display: 'none' } }}
								variant={'outlined'}
								disabled={disabled}
								onBlur={(e) => {
									field.onBlur(e);
									onBlur && onBlur(e.target.value);
								}}
								onChange={(e) => {
									let inputValue = e.target.value;
									if (isCurrencyText) {
										let numericValue = inputValue.replace(/[^0-9.\-]/g, ''); // Allow numbers, dot, and negative sign
										numericValue = numericValue.replace(/(?!^)-/g, ''); // Remove any '-' that is not at the start
										inputValue = numericValue.replace(/,/g, '');
									}
									if (!preventSetValue) {
										form.setFieldValue(field.name, inputValue);
									}
									onChanges && onChanges(inputValue);
								}}
								size="small"
								onClick={(e) => {
									e.stopPropagation();
								}}
								InputProps={InputProps}
								minRows={rows}
								fullWidth={true}
								className={inputClassName}
								type={type ? type : showCurrency ? 'number' : 'text'}
								name={field?.name}
								value={isCurrencyText && field.value
									? field.value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') // Format with commas
									: field.value}
								{...props}
							/>
						</Box>
					</FormGroup>
				);
			}}
		</FastField>
	);
}
